import { useCallback, useEffect, useRef, useState } from 'react'
import { getTimeline } from './api'
import type { RelationRef, TimelineCounts, TimelineItem, TimelineType } from './types'

export function itemKey(item: TimelineItem): string {
  return item.kind === 'activity' || item.kind === 'task' ? `${item.kind}:${item.id}` : `${item.kind}:${item.key}`
}

interface Loaded {
  /** The query this belongs to: relation, types, search, members, page size. */
  scope: string
  items: TimelineItem[]
  nextCursor: string | null
  status: 'ready' | 'error'
  loadingMore: boolean
}

const NO_ITEMS: TimelineItem[] = []

/** The `types` option travels through the hooks as a comma-joined key (a stable dependency). */
function typesFromKey(key: string): TimelineType[] | undefined {
  return key ? (key.split(',') as TimelineType[]) : undefined
}

/**
 * One relation's timeline: the first page whenever the query or `version` changes,
 * further pages on `loadMore` (merged by key, so an item is never shown twice), and
 * local `replace`/`remove` after an edit or delete. A newer query wins: answers to an
 * older one are dropped (requests can finish out of order).
 *
 * "Loading" is worked out by comparing the loaded scope with the wanted one, not set from
 * the effect: a new query shows the skeleton, while a refresh of the same query (`version`,
 * `reload`) keeps the lines on screen until the new page replaces them.
 */
export function useTimeline(
  relation: RelationRef,
  options: { types: TimelineType[] | null; q: string; includeMembers: boolean; limit: number; version: number },
) {
  const { kind, id } = relation
  const { q, includeMembers, limit, version } = options
  const typesKey = options.types?.join(',') ?? ''
  const relationKey = `${kind}:${id}`
  const scope = JSON.stringify([relationKey, typesKey, q, includeMembers, limit])
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [countsState, setCountsState] = useState<{ relationKey: string; counts: TimelineCounts } | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  // Numbers the first-page requests: an answer to `loadMore` is only used while no newer one began.
  const generation = useRef(0)

  useEffect(() => {
    generation.current += 1
    const controller = new AbortController()
    getTimeline(
      { kind, id },
      { types: typesFromKey(typesKey), q, cursor: null, limit, includeMembers },
      controller.signal,
    )
      .then((page) => {
        if (controller.signal.aborted) return
        setLoaded({ scope, items: page.items, nextCursor: page.next_cursor, status: 'ready', loadingMore: false })
        // The counts come with the first page and stay while the next query is on its way.
        if (page.counts) setCountsState({ relationKey, counts: page.counts })
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setLoaded({ scope, items: [], nextCursor: null, status: 'error', loadingMore: false })
      })
    return () => controller.abort()
  }, [kind, id, relationKey, typesKey, q, includeMembers, limit, version, reloadKey, scope])

  const view = loaded !== null && loaded.scope === scope ? loaded : null
  const cursor = view?.nextCursor ?? null
  const busy = view?.loadingMore ?? false

  const loadMore = useCallback(() => {
    if (!cursor || busy) return
    const mine = generation.current
    setLoaded((current) => (current !== null && current.scope === scope ? { ...current, loadingMore: true } : current))
    getTimeline(
      { kind, id },
      { types: typesFromKey(typesKey), q, cursor, limit, includeMembers },
    )
      .then((page) => {
        if (mine !== generation.current) return
        setLoaded((current) => {
          if (current === null || current.scope !== scope) return current
          const seen = new Set(current.items.map(itemKey))
          return {
            ...current,
            items: [...current.items, ...page.items.filter((item) => !seen.has(itemKey(item)))],
            nextCursor: page.next_cursor,
            loadingMore: false,
          }
        })
      })
      .catch(() => {
        if (mine !== generation.current) return
        setLoaded((current) => (current !== null && current.scope === scope ? { ...current, loadingMore: false } : current))
      })
  }, [cursor, busy, scope, kind, id, typesKey, q, limit, includeMembers])

  return {
    items: view?.items ?? NO_ITEMS,
    counts: countsState !== null && countsState.relationKey === relationKey ? countsState.counts : null,
    nextCursor: cursor,
    status: view === null ? ('loading' as const) : view.status,
    loadingMore: busy,
    loadMore,
    reload: () => {
      // A page that failed shows the skeleton again while it is retried.
      setLoaded((current) => (current !== null && current.status === 'error' ? null : current))
      setReloadKey((key) => key + 1)
    },
    replace: (item: TimelineItem) =>
      setLoaded((current) =>
        current === null
          ? current
          : { ...current, items: current.items.map((old) => (itemKey(old) === itemKey(item) ? item : old)) },
      ),
    remove: (key: string) => {
      const gone = view?.items.find((old) => itemKey(old) === key)
      setLoaded((current) =>
        current === null ? current : { ...current, items: current.items.filter((old) => itemKey(old) !== key) },
      )
      // The chip counts follow at once; a reload brings the exact numbers.
      if (gone?.kind === 'activity') {
        const { type } = gone
        setCountsState((current) =>
          current === null
            ? current
            : { ...current, counts: { ...current.counts, [type]: Math.max(0, current.counts[type] - 1) } },
        )
      }
    },
  }
}
